// frontend/src/components/LOS/Caprion/CaprionAAStatus.tsx
//
// Caprion Account Aggregator (AA) Status — Ignosis FIU
// =====================================================
//
// This component is shown AFTER the customer has completed the Ignosis AA journey
// (approved consent in the AA portal). It polls the backend status API until
// Ignosis confirms the bank data has been fetched successfully.
//
// Flow:
//  1. Component mounts → starts polling GET /api/caprion/aa/status/ every 4 seconds
//     → Backend calls Ignosis API 2: GET /web/status?trackingId=&referenceId=
//        (referenceId = UNDERWRITING referenceId only, per Ignosis docs)
//  2. Shows animated "Fetching your banking information…" loading screen
//  3. On is_complete == true (status == COMPLETED AND dataFetchStatus == COMPLETED):
//     → Calls POST /api/caprion/aa/fetch-data/
//        → Backend calls Ignosis API 3: GET /web/fetch/json (raw bank data)
//        → Backend calls Ignosis API 4: GET /web/fetch/analytics/json (analytics)
//        → Both responses stored in CaprionAASession DB record
//     → Shows success screen → calls onDone()
//  4. On is_failed == true → shows error with retry option

import React, { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
  isBuilderMode?: boolean;
}

type Phase = "polling" | "fetching_data" | "success" | "failed" | "error";

const POLL_INTERVAL_MS  = 4000;   // poll every 4 seconds
const MAX_POLL_ATTEMPTS = 75;     // max 5 min (75 × 4s)

// ── Loading dots animation ────────────────────────────────────────────────────
const LoadingDots: React.FC = () => {
  const [dots, setDots] = useState(".");
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? "." : d + ".")), 500);
    return () => clearInterval(t);
  }, []);
  return <span>{dots}</span>;
};

// ── Step indicator ────────────────────────────────────────────────────────────
interface StepProps {
  label: string;
  status: "pending" | "active" | "done";
}
const Step: React.FC<StepProps> = ({ label, status }) => {
  const icon =
    status === "done"   ? "✅" :
    status === "active" ? (
      <svg className="animate-spin h-4 w-4 text-intellend-green inline" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
      </svg>
    ) : "⏳";

  return (
    <li className={`flex items-center gap-3 text-sm ${status === "pending" ? "text-gray-400" : "text-gray-700"}`}>
      <span className="w-5 text-center">{icon}</span>
      <span className={status === "active" ? "font-semibold text-intellend-green" : ""}>{label}</span>
    </li>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
const CaprionAAStatus: React.FC<Props> = ({ onDone, isBuilderMode = false }) => {
  const startedRef    = useRef(false);
  const pollCountRef  = useRef(0);
  const intervalRef   = useRef<ReturnType<typeof setInterval> | null>(null);

  const [phase, setPhase]     = useState<Phase>("polling");
  const [message, setMessage] = useState("Fetching your banking information…");
  const [error, setError]     = useState<string | null>(null);
  // Track which steps are done for the step indicator
  const [consentDone,  setConsentDone]  = useState(true);   // already done (user came from consent page)
  const [statusDone,   setStatusDone]   = useState(false);
  const [fetchDone,    setFetchDone]    = useState(false);

  // ── Fetch raw data + analytics once status is COMPLETED ───────────────────
  const fetchData = async () => {
    setPhase("fetching_data");
    setMessage("Downloading bank statements and analytics…");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/caprion/aa/fetch-data/`, {});
      if (res.data?.success === false) {
        throw new Error(res.data?.error || "Data fetch failed.");
      }
      setFetchDone(true);
      setPhase("success");
      setTimeout(() => onDone?.(), 1500);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        (err instanceof Error ? err.message : String(err));
      console.error("[CaprionAAStatus] fetchData error:", msg);
      // Non-fatal — data fetch failed but consent was approved; still advance
      console.warn("[CaprionAAStatus] Data fetch failed, advancing anyway:", msg);
      setFetchDone(false);
      setPhase("success");
      setTimeout(() => onDone?.(), 1500);
    }
  };

  // ── Poll status ───────────────────────────────────────────────────────────
  const pollStatus = async () => {
    pollCountRef.current += 1;

    if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
      stopPolling();
      setPhase("failed");
      setError("AA status check timed out. Please try again.");
      return;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/caprion/aa/status/`);
      const {
        status,
        data_fetch_status,
        message: msg,
        is_complete,
        is_failed,
      } = res.data || {};

      console.info(
        `[CaprionAAStatus] Poll #${pollCountRef.current}: status=${status} ` +
        `data_fetch_status=${data_fetch_status} is_complete=${is_complete} is_failed=${is_failed}`
      );

      if (msg) setMessage(msg);

      if (is_complete) {
        stopPolling();
        setStatusDone(true);
        // Trigger data + analytics fetch
        await fetchData();
        return;
      }

      if (is_failed) {
        stopPolling();
        setPhase("failed");
        setError(msg || "AA journey failed or expired. Please try again.");
        return;
      }
    } catch (err: any) {
      // Network errors during polling — log but keep polling (transient)
      console.warn("[CaprionAAStatus] Poll error (will retry):", err?.message);
    }
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // ── Start polling on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // First poll immediately, then every POLL_INTERVAL_MS
    pollStatus();
    intervalRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Builder mode preview ──────────────────────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">📊</div>
        <p className="font-semibold text-gray-500">Caprion: AA Status (Ignosis)</p>
        <p className="text-xs mt-1">
          Polls Ignosis status every 4s (UNDERWRITING referenceId). On COMPLETED, fetches
          raw JSON (API 3) + analytics JSON (API 4) and stores both in DB. Advances automatically.
        </p>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (phase === "success") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-6">
            <div className="text-6xl">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800">Bank Data Fetched!</h2>
            <p className="text-sm text-gray-500">
              Your bank statements have been successfully fetched via Account Aggregator.
              Your loan application will now be processed.
            </p>
            <div className="bg-intellend-green/5 border border-intellend-green/20 rounded-lg p-4">
              <p className="text-sm font-semibold text-intellend-green">
                ✅ Bank data received successfully
              </p>
              <p className="text-xs text-intellend-green/70 mt-1">
                Proceeding to the next step…
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Failed ────────────────────────────────────────────────────────────────
  if (phase === "failed") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-600">AA Fetch Failed</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left space-y-1">
              <p className="text-sm font-semibold text-red-700">❌ Error</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">
                Please go back and re-initiate the AA journey.
              </p>
            </div>
            <button
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
              onClick={() => {
                // Reset and retry polling
                startedRef.current  = false;
                pollCountRef.current = 0;
                setPhase("polling");
                setError(null);
                setStatusDone(false);
                setFetchDone(false);
                setMessage("Fetching your banking information…");
                pollStatus();
                intervalRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
              }}
            >
              Retry Status Check
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Fetching data (API 3 + 4 in progress) ────────────────────────────────
  const isFetchingData = phase === "fetching_data";

  // ── Polling (default) ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">

          {/* Header */}
          <div className="text-center space-y-2">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">
              {isFetchingData
                ? "Downloading Bank Data"
                : <>Fetching Your Banking Information<LoadingDots /></>
              }
            </h2>
            <p className="text-sm text-gray-500">{message}</p>
          </div>

          {/* Animated progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 bg-intellend-green rounded-full animate-pulse"
              style={{ width: isFetchingData ? "85%" : "60%" }}
            />
          </div>

          {/* Steps */}
          <ul className="space-y-3 bg-gray-50 border border-gray-100 rounded-lg p-4">
            <Step
              label="AA consent approved"
              status={consentDone ? "done" : "active"}
            />
            <Step
              label="Connecting to your bank & verifying consent"
              status={statusDone ? "done" : "active"}
            />
            <Step
              label="Fetching bank statements"
              status={statusDone && !isFetchingData && !fetchDone ? "active"
                    : fetchDone ? "done"
                    : isFetchingData ? "active"
                    : "pending"}
            />
            <Step
              label="Processing analytics"
              status={fetchDone ? "done" : isFetchingData ? "active" : "pending"}
            />
            <Step
              label="Ready for loan assessment"
              status="pending"
            />
          </ul>

          {/* Info note */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <p className="text-xs text-blue-700 text-center">
              ⏳ This usually takes 30–90 seconds. Please keep this page open.
            </p>
          </div>

          <p className="text-sm text-center font-medium text-gray-700">
            🔒 Secured by RBI-regulated Account Aggregator framework · Powered by Ignosis
          </p>
        </div>
      </main>
    </div>
  );
};

export default CaprionAAStatus;
